/**
 * The server's reference forms (`docs/components/content.md` D-2, `server/src/dmw/kernel/refs.py`),
 * read and written by the web app:
 *
 * - a document version, `<item id>@<blob id>` (`VersionRef`);
 * - one segment of it, `<item id>@<blob id>#<segment path>` (`SegmentRef`), where the path is
 *   steps such as `subentry.2/sentence.1`;
 * - a corpus version, `<corpus id>@<commit id>` (`CollectionVersionRef`).
 *
 * The app splits references structurally only: one `@`, at most one `#`, no blanks. The
 * server checks IDs, object IDs and segment steps, so the rules live in one place.
 */
export const VERSION_SEPARATOR = "@";
export const SEGMENT_SEPARATOR = "#";

/** One exact document version: the reference every link and result carries (NFR-16). */
export interface DocumentVersionRef {
  readonly documentId: string;
  readonly version: string;
}

/** A document as an address names it. `version` is `null` for "the latest version". */
export interface DocumentAddress {
  readonly documentId: string;
  readonly version: string | null;
}

/** A document address, and the segment to show in it, if any. */
export interface ContentAddress {
  readonly document: DocumentAddress;
  readonly segmentPath: string | null;
}

export interface CollectionVersionRef {
  readonly collectionId: string;
  readonly commit: string;
}

/** Read `<id>[@<version>]`. Returns `null` for anything that is not one ID with at most one version. */
export function parseDocumentAddress(text: string | undefined): DocumentAddress | null {
  const parts = splitAtVersion(text);
  return parts === null ? null : { documentId: parts.identifier, version: parts.version };
}

/** Read `<id>[@<version>][#<segment path>]`, as typed by a user or pasted from a result. */
export function parseContentAddress(text: string): ContentAddress | null {
  const [documentText, segmentPath, ...rest] = text.trim().split(SEGMENT_SEPARATOR);
  if (rest.length > 0 || segmentPath === "") {
    return null;
  }
  const document = parseDocumentAddress(documentText);
  return document === null ? null : { document, segmentPath: segmentPath ?? null };
}

/** Read an exact `<item id>@<blob id>`; `null` without a version. */
export function parseDocumentVersionRef(text: string): DocumentVersionRef | null {
  const address = parseDocumentAddress(text);
  return address?.version == null ? null : { documentId: address.documentId, version: address.version };
}

export function parseCollectionVersionRef(text: string): CollectionVersionRef | null {
  const parts = splitAtVersion(text);
  return parts?.version == null ? null : { collectionId: parts.identifier, commit: parts.version };
}

export function formatCollectionVersionRef(reference: CollectionVersionRef): string {
  return `${reference.collectionId}${VERSION_SEPARATOR}${reference.commit}`;
}

export function formatDocumentAddress(address: DocumentAddress): string {
  return address.version === null ? address.documentId : formatDocumentVersionRef({ ...address, version: address.version });
}

export function formatDocumentVersionRef(reference: DocumentVersionRef): string {
  return `${reference.documentId}${VERSION_SEPARATOR}${reference.version}`;
}

export function formatSegmentRef(document: DocumentVersionRef, segmentPath: string): string {
  return `${formatDocumentVersionRef(document)}${SEGMENT_SEPARATOR}${segmentPath}`;
}

function splitAtVersion(
  text: string | undefined,
): { readonly identifier: string; readonly version: string | null } | null {
  if (text === undefined) {
    return null;
  }
  const trimmed = text.trim();
  const [identifier, version, ...rest] = trimmed.split(VERSION_SEPARATOR);
  if (identifier === undefined || identifier === "" || rest.length > 0 || /\s/.test(trimmed)) {
    return null;
  }
  if (version === undefined) {
    return { identifier, version: null };
  }
  return version === "" ? null : { identifier, version };
}
